/** @license
 *
 * Copyright 2018 Esri
 *
 * All rights reserved under the copyright laws of the United States and applicable international laws, treaties,
 * and conventions.
 *
 * This material is licensed for use under the Esri Master License Agreement (MLA), and is bound by the terms of
 * that agreement. You may redistribute and use this code without modification, provided you adhere to the terms
 * of the MLA and include this copyright notice.
 *
 * See use restrictions at http://www.esri.com/legal/pdfs/mla_e204_e300/english
 *
 * For additional information, contact: Environmental Systems Research Institute, Inc. Attn: Contracts and Legal
 * Services Department 380 New York Street Redlands, California, USA 92373 USA
 *
 * email: contracts@esri.com
 */

import * as go from "gojs";
import { getItemIcon } from "./getItemIcon"
import { injectGlobal } from "@emotion/css";

export interface IConnectedNodesAndLinks {
  connectedNodes: Set<go.Node>;
  connectedLinks: Set<go.Link>;
}

export interface IKeyedAdornment {
  [key: string]: go.Adornment;
}

export interface IKeyedValue {
  [key: string]: any;
}

export interface ILink {
  from: string;
  fromSpot: go.Spot;
  to: string;
  toSpot: go.Spot;
}

interface INavigationButtons {
  resetButton: HTMLCalciteActionElement;
  centerButton: HTMLCalciteActionElement;
  zoomInButton: HTMLCalciteActionElement;
  zoomOutButton: HTMLCalciteActionElement;
}

export interface INode {
  key: string;
  title: string;
  itemId: string;
  itemType: string;
  itemTypeForDisplay: string;
  itemTypeKeywords: string[];
  group?: string;
  url?: string;
}

export interface INodesAndLinks {
  nodes: INode[],
  links: ILink[],
  groupPseudoNodes: string[]
}

export interface ISolutionGraphTranslations {
  centerDiagram: string;
  filteredView: string;
  reset: string;
  zoomIn: string;
  zoomOut: string;
}

export interface ITemplateInfoGraph {
  id: string;
  title: string;
  type: string;
  typeKeywords: string[];
  snippet: string;
  dependencies: string[];
  url?: string;
}

const cColumnSpacing = 15;
const cLayerSpacing = 15;
const cDiagramPadding = 10;

const cNodeSize = new go.Size(220, 48);
const cNodeBorderStrokeWidth = 1;
const cNodeCornerRadius = 8;
const cNodeIconPanelSize = new go.Size(48, 48);
const cNodeIconSize = new go.Size(16, 16);
const cNodeTextLineSize = new go.Size(153, 17);
const cNodeTextFormat = "400 0.75rem 'Avenir Next', Avenir, 'Helvetica Neue', sans-serif";
const cNodeTextFormatBold = "700 0.75rem 'Avenir Next', Avenir, 'Helvetica Neue', sans-serif";

const cDiagramBackgroundColor = "#f4f4f4";
const cNodeBorderColor = "#cacaca";
const cNodeBackgroundColor = "#ffffff";
const cNodeTextColor = "#4a4a4a";
const cLinkColor = "#6a6a6a";
const cTooltipBackgroundColor = "#ffffff";

const cNormalNodeOpacity = 1;
const cDimmedNodeOpacity = 0.3;
const cNormalLinkOpacity = 1;
const cDimmedLinkOpacity = 0.2;
const cHighlightedLinkOpacity = 1;

// ------------------------------------------------------------------------------------------------------------------ //

/**
 * Configures a directed graph of the Solution items in the collection.
 * @param collection Items contained in a Solution
 * @param graphicContainer HTML element to contain graph div
 * @return Configured diagram object
 */
export function defineDiagram(
  graphicContainer: HTMLElement
): go.Diagram {
  // Create the container for the graph; element is taken over by graphing software
  const diagramDiv = document.createElement("div");
  diagramDiv.style.width = "100%";
  diagramDiv.style.height = "100%";

  // Add the containers to the provided container
  graphicContainer.appendChild(diagramDiv);

  // Draw solution items as a directed graph
  const diagram = new go.Diagram(
    diagramDiv, // create a Diagram for the HTML Div element
    {
      click: dispatchDiagramClickEvent, // handle diagram clicks
      contentAlignment: go.Spot.Center, // align document to the center of the viewport
      dragSelectingTool: null,  // disable drag selection
      hoverDelay: 50,  // milliseconds before mouse-hover displays information
      initialAutoScale: go.AutoScale.Uniform, // automatic zoom-to-fit
      layout: new go.LayeredDigraphLayout({
        columnSpacing: cColumnSpacing,
        layerSpacing: cLayerSpacing
      }),
      padding: cDiagramPadding,
      scrollMode: go.ScrollMode.Infinite,  // allow infinite scrolling--beyond document bounds
      "toolManager.hoverDelay": 50,  // 50 milliseconds before tooltip appears
      "toolManager.mouseWheelBehavior": go.WheelMode.Zoom,  // mouse wheel zooms instead of scrolls vertically
    }
  );

  diagram.nodeTemplate = new go.Node("Auto", {
    click: dispatchNodeClickEvent,
    movable: false,
    opacity: cNormalNodeOpacity,
    toolTip: new go.Adornment("Auto", {
      isShadowed: true,
      margin: new go.Margin(12, 16, 6, 16),  // top, right, bottom, left
      padding: 0,
      shadowBlur: 16,
      shadowColor: "rgba(0, 0, 0, 0.16)",
      shadowOffset: new go.Point(0, 0)
    })
      .add(new go.Panel("Vertical", { background: cTooltipBackgroundColor, margin: 0 })
      .add(new go.TextBlock({ margin: 14 })
      .bind("text", "title")))
  })
    .bind("text", "title")
    .add(
      new go.Shape({
        desiredSize: cNodeSize,
        fill: cNodeBackgroundColor,
        geometryString: createRoundedRectangleGeometryPath(cNodeSize.width, cNodeSize.height, cNodeCornerRadius,
          cNodeIconPanelSize.width, !!cNodeBackgroundColor),
        height: cNodeSize.height,
        portId: "",
        stroke: cNodeBorderColor,
        strokeWidth: cNodeBorderStrokeWidth,
        width: cNodeSize.width
      })
    )
    .add(
      // Node content
      new go.Panel("Horizontal", {
        alignment: go.Spot.Left,
        stretch: go.Stretch.Horizontal
      })
      .add(
          // Icon panel
          new go.Panel("Horizontal", {
            alignment: go.Spot.Left,
            desiredSize: cNodeIconPanelSize,
            margin: 1
          })
            .add(
              new go.Panel("Horizontal", {
                alignment: go.Spot.Center,
                margin: 16
              })
                .add(
                  new go.Picture({
                    desiredSize: cNodeIconSize
                  })
                    .bind("source", "", (node: any) => getIconUrl(node.itemType as string, node.itemTypeKeywords as string[]))
                ),
            ),
          // Label
          new go.Panel("Horizontal", {
            alignment: go.Spot.Right,
            margin: new go.Margin(4, 8, 0, 8)  // top, right, bottom, left
          })
            .add(
              new go.Panel("Vertical", {
              })
                .add(
                  // First line
                  new go.TextBlock({
                    alignment: go.Spot.Left,
                    desiredSize: cNodeTextLineSize,
                    font: cNodeTextFormatBold,
                    overflow: go.TextOverflow.Ellipsis,
                    stroke: cNodeTextColor,
                    wrap: go.Wrap.None
                  })
                    .bind("text", "title"),
                  // Second line
                  new go.TextBlock({
                    alignment: go.Spot.Left,
                    desiredSize: cNodeTextLineSize,
                    font: cNodeTextFormat,
                    overflow: go.TextOverflow.Ellipsis,
                    stroke: cNodeTextColor,
                    wrap: go.Wrap.None
                  })
                    .bind("text", "itemTypeForDisplay")
                )
            )
        )
      )

  diagram.commandHandler.zoomFactor = 1.05;  // 5% increase/decrease
  diagram.setDivBackground(cDiagramBackgroundColor);

  // Control mouse click behavior
  const selectingTool = diagram.toolManager.clickSelectingTool;
  selectingTool.standardMouseSelect = function () {};  // cannot be an arrow function

  const draggingTool = diagram.toolManager.draggingTool;
  draggingTool.isEnabled = false;

  // Customize the link display
  diagram.linkTemplate = new go.Link({
    corner: 5,
    movable: false,
    routing: go.Routing.Orthogonal,
    selectable: false
  })
    .add(
      new go.Shape({ // the link path shape
        opacity: cNormalLinkOpacity,
        stroke: cLinkColor
      }),
      new go.Shape({ // the "to" arrowhead
        fill: cLinkColor,
        opacity: cNormalLinkOpacity,
        stroke: null,
        toArrow: "Standard"
      })
    );

  return diagram;
}

/**
 * Update a diagram with a new set of nodes and links.
 * @param diagram Diagram whose model is to be udpated with a new set of nodes and links
 * @param nodesAndLinks Nodes and links to display in the graph
 */
export function drawDiagram(
  diagram: go.Diagram,
  nodesAndLinks: INodesAndLinks,
): void {
  // Set the model data for the diagram
  diagram.model = new go.GraphLinksModel(nodesAndLinks.nodes, nodesAndLinks.links, {
    linkFromPortIdProperty: "fromPort",
    linkToPortIdProperty: "toPort"
  });
}

/**
 * Draws a navigation bar and connects it to the diagram.
 * @param graphicContainer HTML element to contain navigation controls
 * @param diagram Diagram to be controlled
 * @param translations Text to use for the navigation controls' tooltips
 * @param includeShowHideGrayedOutButton If true, the "Filter selected view" switch will be displayed
 */
export function drawAndConnectControlBars(
  graphicContainer: HTMLElement,
  diagram: go.Diagram,
  translations: ISolutionGraphTranslations,
  includeFilteredViewButton = true
): void {
  if (includeFilteredViewButton) {
    // Draw control bar
    const filterViewButton: HTMLCalciteSwitchElement = drawfilteredViewBar(graphicContainer, translations);

    // Add event listeners to the controls
    filterViewButton.addEventListener("calciteSwitchChange",
      () => document.dispatchEvent(new CustomEvent("filteredViewChanged", { detail:  filterViewButton.checked }))
    );
  }

  // Draw control bar
  const { resetButton, centerButton, zoomInButton, zoomOutButton } = drawNavigationBar(graphicContainer, translations);

  // Add event listeners to the controls
  resetButton.addEventListener("click", () => reset(diagram));
  centerButton.addEventListener("click", () => diagram.zoomToFit());
  zoomInButton.addEventListener("click", () => diagram.commandHandler.increaseZoom());
  zoomOutButton.addEventListener("click", () => diagram.commandHandler.decreaseZoom());
}

/**
 * Draws a directed graph of the Solution items in the collection.
 * @param graphicContainer HTML element to contain graph and navigation controls
 * @param nodesAndLinks Nodes and links to display in the graph
 * @param translations Translations for the navigation controls
 * @returns Created diagram
 */
export function drawSolutionGraph(
  graphicContainer: HTMLElement,
  nodesAndLinks: INodesAndLinks,
  translations: ISolutionGraphTranslations,
): go.Diagram {
  setStyles();

  // Draw solution items as a directed graph
  const diagram = defineDiagram(graphicContainer);
  drawDiagram(diagram, nodesAndLinks);

  // Draw the navigation controls
  drawAndConnectControlBars(graphicContainer, diagram, translations);

  return diagram;
}

/**
 * Gets the nodes and links connected to the supplied node.
 * @param diagramNode Node whose connected nodes and links are to be found
 * @returns List of connected nodes and links
 */
export function getConnectedNodesAndLinks(diagramNode: go.GraphObject): IConnectedNodesAndLinks {
  const connectedNodes: Set<go.Node> = new Set();
  const connectedLinks: Set<go.Link> = new Set();

  // Trace "from" links
  function traceFromLinks(node: go.Node): void {
    node.findLinksInto().each(
      link => {
        connectedLinks.add(link);
        const fromNode = link.fromNode;
        if (fromNode && !connectedNodes.has(fromNode)) {
          connectedNodes.add(fromNode);
          traceFromLinks(fromNode);
        }
      }
    );
  }
  traceFromLinks(diagramNode as go.Node);

  // Trace "to" links
  function traceToLinks(node: go.Node): void {
    node.findLinksOutOf().each(
      link => {
        connectedLinks.add(link);
        const toNode = link.toNode;
        if (toNode && !connectedNodes.has(toNode)) {
          connectedNodes.add(toNode);
          traceToLinks(toNode);
        }
      }
    );
  }
  traceToLinks(diagramNode as go.Node);

  return { connectedNodes, connectedLinks };
}

/**
 * Creates the model data that will be represented by Nodes and Links in the diagram.
 * @param collection Items contained in a Solution
 * @param canClickToOpen If true, the node will have a URL to open the item in the portal
 * @param clickToOpenBaseUrl Base URL to open the item in the portal to be used in `${clickToOpenBaseUrl}?id=${itemId}`
 * @returns A structure containing the nodes and links
 */
export function getNodesAndLinks(
  collection: ITemplateInfoGraph[],
  canClickToOpen = false,
  clickToOpenBaseUrl?: string
): INodesAndLinks {
  // Use pseudo nodes for groups
  const groupPseudoNodes: string[] = [];

  // Add the nodes to a list for the diagram
  const nodes: INode[] = collection
    .map((template: ITemplateInfoGraph) => {
      if (template.type !== "Group") {
        const templateTag = createGraphTag(template);
        return createGraphNode(template, templateTag, canClickToOpen, clickToOpenBaseUrl);
      } else {
        groupPseudoNodes.push(template.id);
        return undefined;
      }
    })
    .filter((node) => node !== undefined) as INode[];  // undefineds filtered out, and so result is cleanly INode[]

  // Create a link from each dependency to its node
  const links: ILink[] = [];
  nodes.forEach((node: INode) => {
    const template = collection.find(entry => entry.id === node.itemId);
    (template?.dependencies || []).forEach((dependencyId: string) => {
      const dependency = nodes.find(entry => entry?.itemId === dependencyId);
      if (dependency) {
        links.push({
          from: dependency.key,
          fromSpot: go.Spot.AllSides,
          to: node.key,
          toSpot: go.Spot.AllSides,
        });
      }
    });
  });

  return { nodes, links, groupPseudoNodes };
}

/**
 * Changes the opacity of the node and its links to highlight or dim the node.
 * @param node Node to highlight
 * @param focus Highlight node and dim all others if true; otherwise, restore all nodes to normal opacity
 * @param highlightBorder Border to highlight around the node
 * @param dimUnrelatedNodes If true, dim nodes not directly connected to the focused node
 */
export function highlightNode(
  focusNode: go.GraphObject,
  focus: boolean,
  highlightBorder?: go.Adornment,
  dimUnrelatedNodes = true
): void {
  const myDiagram = focusNode.diagram;
  if (!myDiagram) {
    return;
  }

  const animationSet: IKeyedValue = {};

  if (dimUnrelatedNodes) {
    // When showing, highlight the node and dim all others
    for(const node of myDiagram.nodes) {
      animationSet[node.key as string] = !focus || node === focusNode ? cNormalNodeOpacity : cDimmedNodeOpacity;
    }

    // When showing, dim all links
    for(const link of myDiagram.links) {
      const key = `${link.fromNode?.key}-${link.toNode?.key}`;  // despite doc, Link.key doesn't work
      animationSet[key] = focus ? cDimmedLinkOpacity : cNormalLinkOpacity;
    }

    // Trace links to re-highlight directly-connected nodes and the links between them
    // Trace "from" links node-to-node until no more are found; repeat for "to" links
    if (focus) {
      const { connectedNodes, connectedLinks }: IConnectedNodesAndLinks = getConnectedNodesAndLinks(focusNode);

      // Highlight the traced links and nodes
      for(const node of connectedNodes) {
        animationSet[node.key as string] = cNormalNodeOpacity;
      }
      for(const link of connectedLinks) {
        const key = `${link.fromNode?.key}-${link.toNode?.key}`;
        animationSet[key] = cHighlightedLinkOpacity;
      }
    }
  }

  // Add a thick border around the node
  if (highlightBorder) {
    highlightBorder.adornedObject = focus? focusNode : null;
  }

  if (dimUnrelatedNodes) {
    // Apply the animations
    const animation = new go.Animation();
    animation.duration = 1;  // milliseconds

    for(const node of myDiagram.nodes) {
      animation.add(node, "opacity", node.opacity, animationSet[node.key as string]);
    }
    for(const link of myDiagram.links) {
      const key = `${link.fromNode?.key}-${link.toNode?.key}`;
      animation.add(link, "opacity", link.opacity, animationSet[key]);
    }

    animation.start();
  }
}

/**
 * Changes the opacity of a collection of nodes highlight or dim the node.
 * @param nodes Nodes to highlight
 * @param focus Highlight node and dim all others if true; otherwise, restore all nodes to normal opacity
 * @param highlightBorders Border adornments for all item nodes
 * @param dimUnrelatedNodes If true, dim nodes not in the nodes list
 */
export function highlightNodes(
  nodeIds: string[],
  nodes: go.GraphObject[],
  focus: boolean,
  highlightBorders: IKeyedAdornment,
  dimUnrelatedNodes = true
): void {
  if (nodeIds.length === 0) {
    return;
  }

  const myDiagram = nodes[0].diagram;
  if (!myDiagram) {
    return;
  }

  const animationSet: IKeyedValue = {};

  if (dimUnrelatedNodes) {
    // When showing, highlight the node and dim all others
    for(const node of myDiagram.nodes) {
      const isFocusNode = nodeIds.includes(node.key as string);
      animationSet[node.key as string] = !focus || isFocusNode ? cNormalNodeOpacity : cDimmedNodeOpacity;
    }

    // When showing, dim all links
    for(const link of myDiagram.links) {
      const key = `${link.fromNode?.key}-${link.toNode?.key}`;  // despite doc, Link.key doesn't work
      animationSet[key] = focus ? cDimmedLinkOpacity : cNormalLinkOpacity;
    }
  }

  // Remove all borders
  Object.keys(highlightBorders).forEach((key: string) => {
    highlightBorders[key].adornedObject = null;
  });

  if (dimUnrelatedNodes) {
    // Apply the animations
    const animation = new go.Animation();
    animation.duration = 1;  // milliseconds

    for(const node of myDiagram.nodes) {
      animation.add(node, "opacity", node.opacity, animationSet[node.key as string]);
    }
    for(const link of myDiagram.links) {
      const key = `${link.fromNode?.key}-${link.toNode?.key}`;
      animation.add(link, "opacity", link.opacity, animationSet[key]);
    }

    animation.start();
  }
}

// ------------------------------------------------------------------------------------------------------------------ //

/**
 * Create a graph node from an item template, fetching the item information if necessary.
 * @param template Item template
 * @param templateTag Tag to use for node
 * @param canClickToOpen If true, the node will have a URL to open the item in the portal
 * @param clickToOpenBaseUrl Base URL to open the item in the portal to be used in `${clickToOpenBaseUrl}?id=${itemId}`
 * @returns Promise resolving to the graph node
 */
function createGraphNode(
  template: ITemplateInfoGraph,
  templateTag: string,
  canClickToOpen = false,
  clickToOpenBaseUrl?: string
): INode {
  return {
    key: templateTag,
    title: template.title,
    itemId: template.id,
    itemType: template.type,
    itemTypeForDisplay: getTypeForDisplay(template.type, template.typeKeywords),
    itemTypeKeywords: template.typeKeywords,
    group: template.type,
    url: canClickToOpen ? `${clickToOpenBaseUrl}?id=${template.id}` : undefined
  };
}

/**
 * Creates a tag for the graph node that combines an abbreviation of the item type with part of the item ID.
 * @param template Item template
 * @returns String containing tag
 */
function createGraphTag(
  template: ITemplateInfoGraph
): string {
  return template.id;
}

/**
 * Creates a geometry string representing a rounded rectangle.
 * @param width Width of rectangle
 * @param height Height of rectangle
 * @param radius Corner radius
 * @param iconPanelWidth Width of icon panel for placing a vertical separator between the icon and the text
 * @param isFilled Background color of the rectangle; if omitted, no background color is set
 * @returns Geometry path string
 * @see https://gojs.net/latest/intro/shapes.html#GeometryPath
 */
function createRoundedRectangleGeometryPath(
  width: number,
  height: number,
  radius: number,
  iconPanelWidth = 0,
  isFilled = false
): string {
  let path = "";
  if (isFilled) {
    path += "F";
  }

  // Draw the rectangle
  const horizontalStraightLineLen = width - (2 * radius);
  const verticalStraightLineLen = height - (2 * radius);
  path += ` M${0} ${0}`;  // Start at top-left corner
  path += ` h${horizontalStraightLineLen}`;
  path += ` a${radius} ${radius} 0 0 1 ${radius} ${radius}`;
  path += ` v${verticalStraightLineLen}`;
  path += ` a${radius} ${radius} 0 0 1 ${-radius} ${radius}`;
  path += ` h${-horizontalStraightLineLen}`;
  path += ` a${radius} ${radius} 0 0 1 ${-radius} ${-radius}`;
  path += ` v${-verticalStraightLineLen}`;
  path += ` a${radius} ${radius} 0 0 1 ${radius} ${-radius}`;

  // Add the separator line
  if (iconPanelWidth > 0) {
    path += ` M${iconPanelWidth - radius} ${0} v${height}`;
  }

  return path;
}

/**
 * Dispatches an event reporting a click on the diagram outside of a node.
 * @param evt Ignored event information
 * @event clickInDiagram Event reporting that a click occurred in the diagram
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function dispatchDiagramClickEvent(evt: go.InputEvent): void {
  document.dispatchEvent(new CustomEvent("clickInDiagram"));
}

/**
 * Dispatches a document click event.
 * @param _evt Ignored event information
 * @param obj Graph object that received click
 * @event clickOnNode Event reporting that a click occurred on a node
 */
function dispatchNodeClickEvent(_evt: go.InputEvent, obj: go.GraphObject): void {
  const node = obj.part;
  if (node instanceof go.Node) {
    node.selectionAdorned = false;
    document.dispatchEvent(new CustomEvent("clickOnNode", { detail: node }));
  }
}

/**
 * Draws a filter view bar.
 * @param graphicContainer HTML element to contain filter view bar div
 * @param translations Translations for the filter view control
 * @returns Filter view switch
 */
function drawfilteredViewBar(
  graphicContainer: HTMLElement,
  translations: ISolutionGraphTranslations,
): HTMLCalciteSwitchElement {
  // Filter view switch
  const filteredViewButton = document.createElement("calcite-switch");
  filteredViewButton.id = "filteredViewButton";
  filteredViewButton.label = translations.filteredView;

  const filteredViewButtonLabel = document.createElement("calcite-label");
  filteredViewButtonLabel.id = "filteredViewButtonLabel";
  filteredViewButtonLabel.setAttribute("layout", "inline");
  filteredViewButtonLabel.innerHTML = translations.filteredView;
  filteredViewButtonLabel.appendChild(filteredViewButton);

  const filteredViewButtonDiv = document.createElement("div");
  filteredViewButtonDiv.id = "filteredViewButtonDiv";
  filteredViewButtonDiv.appendChild(filteredViewButtonLabel);

  // Action pad
  const actionPad = document.createElement("calcite-action-pad");
  actionPad.id = "filter-bar-controls";
  actionPad.layout = "horizontal";
  actionPad.expandDisabled = true;
  actionPad.appendChild(filteredViewButtonDiv);

  // Add the action pad to the graphic container
  const filterBar = document.createElement("div");
  filterBar.id = "filter-bar";
  graphicContainer.appendChild(filterBar);

  filterBar.appendChild(actionPad);
  return filteredViewButton;
}

/**
 * Draws a navigation bar.
 * @param graphicContainer HTML element to contain navigation bar div
 * @param translations Translations for the navigation controls
 * @returns Object with the navigation buttons
 */
function drawNavigationBar(
  graphicContainer: HTMLElement,
  translations: ISolutionGraphTranslations,
): INavigationButtons {
  // Reset diagram button
  const resetButton = document.createElement("calcite-action");
  resetButton.icon = "reset";
  resetButton.id = "showHideGrayedOutButton";
  const resetButtonTooltip = document.createElement("calcite-tooltip");
  resetButtonTooltip.id = "resetButtonTooltip";
  resetButtonTooltip.innerHTML = translations.reset;
  resetButtonTooltip.overlayPositioning = "fixed";
  resetButtonTooltip.placement = "bottom";
  resetButtonTooltip.referenceElement = resetButton.id;

  // Center diagram button
  const centerButton = document.createElement("calcite-action");
  centerButton.icon = "zoom-to-object";
  centerButton.id = "centerButton";
  const centerButtonTooltip = document.createElement("calcite-tooltip");
  centerButtonTooltip.id = "centerButtonTooltip";
  centerButtonTooltip.innerHTML = translations.centerDiagram;
  centerButtonTooltip.overlayPositioning = "fixed";
  centerButtonTooltip.placement = "bottom";
  centerButtonTooltip.referenceElement = centerButton.id;

  // Zoom in button
  const zoomInButton = document.createElement("calcite-action");
  zoomInButton.icon = "plus";
  zoomInButton.id = "zoomInButton";
  const zoomInButtonTooltip = document.createElement("calcite-tooltip");
  zoomInButtonTooltip.id = "zoomInButtonTooltip";
  zoomInButtonTooltip.innerHTML = translations.zoomIn;
  zoomInButtonTooltip.overlayPositioning = "fixed";
  zoomInButtonTooltip.placement = "bottom";
  zoomInButtonTooltip.referenceElement = zoomInButton.id;

  // Zoom out button
  const zoomOutButton = document.createElement("calcite-action");
  zoomOutButton.icon = "minus";
  zoomOutButton.id = "zoomOutButton";
  const zoomOutButtonTooltip = document.createElement("calcite-tooltip");
  zoomOutButtonTooltip.id = "zoomOutButtonTooltip";
  zoomOutButtonTooltip.innerHTML = translations.zoomOut;
  zoomOutButtonTooltip.overlayPositioning = "fixed";
  zoomOutButtonTooltip.placement = "bottom";
  zoomOutButtonTooltip.referenceElement = zoomOutButton.id;

  // Action pad
  const actionPad = document.createElement("calcite-action-pad");
  actionPad.id = "graph-navigation-controls";
  actionPad.layout = "horizontal";
  actionPad.expandDisabled = true;
  actionPad.appendChild(resetButton);
  actionPad.appendChild(resetButtonTooltip);
  actionPad.appendChild(centerButton);
  actionPad.appendChild(centerButtonTooltip);
  actionPad.appendChild(zoomInButton);
  actionPad.appendChild(zoomInButtonTooltip);
  actionPad.appendChild(zoomOutButton);
  actionPad.appendChild(zoomOutButtonTooltip);

  // Add the action pad to the graphic container
  const graphNavigationBar = document.createElement("div");
  graphNavigationBar.id = "graph-navigation-bar";
  graphicContainer.appendChild(graphNavigationBar);

  graphNavigationBar.appendChild(actionPad);
  return { resetButton, centerButton, zoomInButton, zoomOutButton };
}

/**
 * Constructs the path to the icon based on type and typeKeyword info.
 * This function was copied and slightly modified from the arcgis-portal-app.
 * @param type The item type
 * @param typeKeywords The item typeKeywords
 * @returns string the asset path for the given item type and typekeywords
 */
function getIconUrl(
  type: string,
  typeKeywords: string[]
): string {
  const icon = getItemIcon(type, typeKeywords);
  return icon === "group" ? `item-icons/${icon}16.png` : `item-icons/${icon}16.svg`;
}

/**
 * Updates the type value to match how we would like it displayed.
 * @param type the current item type
 * @param typeKeywords the type keywords for the current item
 * @returns the updated type value
 */
function getTypeForDisplay(
  type: string,
  typeKeywords: string[]
): string {
  let _type = type;
  if (type === "Feature Service") {
    const isView = typeKeywords.indexOf("View Service") > -1;
    const isTable = typeKeywords.indexOf("Table") > -1
    _type = isView && isTable ? "Table (hosted, view)" : isTable ? "Table (hosted)" :
      isView ? "Feature Layer (hosted, view)" : "Feature Layer (hosted)";
  }

  if (type === "Web Mapping Application") {
    _type = typeKeywords.indexOf("configurableApp") > -1 ?
      "Instant App" : _type;
  }

  _type = type === "Geoprocessing Service" ? "Tool" : _type;

  _type = type === "Feed" ? "Feed (hosted)" : _type;

  _type = type === "Real Time Analytic" ? "Real Time Analytic (hosted)" : _type;

  _type = type === "Big Data Analytic" ? "Big Data Analytic (hosted)" : _type;

  _type = type === "Vector Tile Service" ? "Tile Layer" : _type;

  return _type;
}

/**
 * Resets the diagram to its initial zoom.
 * @param diagram Diagram to reset
 * @event reset Event reporting that the diagram was reset
 */
function reset(
  diagram: go.Diagram
): void {
  diagram.zoomToFit();
  document.dispatchEvent(new CustomEvent("reset"));
}

/**
 * Adds styles for the graphic to the page.
 */
function setStyles() {
  injectGlobal`
    #filter-bar {
      background-color: #f4f4f4;
      position: absolute;
      top: 16px;
      right: 220px;
      z-index: 100;
    }
    #filteredViewButtonDiv {
      padding: 16px 16px 4px 16px;
    }
    #graph-navigation-bar {
      background-color: #f4f4f4;
      position: absolute;
      top: 16px;
      right: 16px;
      z-index: 100;
    }
    #graph-navigation-controls {
      position: absolute;
      top: 0;
      right: 0;
    }
  `
}
